import { withExternalOverlay } from "@/lib/external-overlay";
import type { GoogleDrivePickerConfig } from "@/lib/google-drive-picker-config";

/**
 * The one Drive scope the chooser asks for (owner decision D1, 2026-11-03).
 *
 * `drive.file` reaches only the files the person picks or uploads through the
 * chooser. The Picker lists the person's whole Drive itself, so browsing does
 * not need a wider scope, and IMAM never reads a file with the token: it goes
 * to `setOAuthToken` and nowhere else. It is also the narrowest scope that can
 * upload, which the course-folder Upload tab needs (D9). It replaced
 * `drive.readonly`, which could read every file the account can see.
 */
export const GOOGLE_DRIVE_FILE_SCOPE = "https://www.googleapis.com/auth/drive.file";

/**
 * The wire values `google.picker.Action` actually carries, read back from the
 * live Picker library on 2026-08-12. The enum's member names are uppercase and
 * its values are not; comparing against the member names silently rejects every
 * real selection, which no fake Picker can reveal because a fake echoes
 * whatever the test hands it.
 */
const PICKER_ACTION_PICKED = "picked";
const PICKER_ACTION_CANCEL = "cancel";
const PICKER_ACTION_ERROR = "error";

/**
 * Ceilings, not deadlines. They exist so an answer that never arrives cannot
 * strand the chooser in its busy state; they are long enough that a person
 * signing in, consenting, and browsing Drive never meets them.
 */
/** Matches the 10s `gapi.load` bound already used for the Picker library. */
export const GOOGLE_DRIVE_SCRIPT_TIMEOUT_MS = 10_000;
export const GOOGLE_DRIVE_TOKEN_TIMEOUT_MS = 120_000;
export const GOOGLE_DRIVE_PICKER_TIMEOUT_MS = 300_000;

const ALLOWED_GOOGLE_HOSTS = new Set([
  "drive.google.com",
  "docs.google.com",
]);
const IDENTIFIER = /^[A-Za-z0-9_-]+$/;
const MIME_TYPE = /^[A-Za-z0-9][A-Za-z0-9!#$&^_.+-]*\/[A-Za-z0-9][A-Za-z0-9!#$&^_.+-]*$/;
const UNSUPPORTED_MIME_TYPES = new Set(["application/vnd.google-apps.folder"]);

export interface GoogleDriveSelection {
  fileUrl: string;
  fileId: string;
  resourceKey: string | null;
  mimeType: string;
  name: string;
  /**
   * Bytes, when Google reports them. Native Google types (Docs, Sheets,
   * Slides) have no meaningful byte size and report none, so this stays null
   * rather than inviting a made-up zero.
   */
  sizeBytes: number | null;
}

/** A durable opening address retains the key Google can return outside its URL.
 * This preserves link access information; it never grants a missing Drive ACL. */
export function googleDriveOpeningUrl(selection: Pick<GoogleDriveSelection, "fileUrl" | "resourceKey">): string {
  if (!selection.resourceKey) return selection.fileUrl;
  const url = new URL(selection.fileUrl);
  if (url.searchParams.getAll("resourcekey").length === 1 && url.searchParams.get("resourcekey") === selection.resourceKey) return selection.fileUrl;
  url.searchParams.set("resourcekey", selection.resourceKey);
  return url.href;
}

/**
 * Why no file came back, as far as the browser can tell. Each is a different
 * thing for the person to do next, so each carries its own words:
 *
 * - `declined`: Google asked for Drive access and the person refused it.
 * - `popup_blocked`: the browser would not open Google's sign-in window.
 * - `no_token`: Google answered without access and without saying why, which
 *   is what a person who is not signed in, or who backed out of the account
 *   chooser, looks like. Never an outage.
 * - `unresponsive`: Google's scripts, token popup or chooser did not answer, or
 *   reported their own error. The only reason that means "Google is down".
 *
 * `kind: "failed"` is unchanged so callers that only ask whether the chooser
 * failed keep compiling; the reason is additive.
 */
export type GoogleDriveFailureReason = "declined" | "popup_blocked" | "no_token" | "unresponsive";

export type GoogleDrivePickerOutcome =
  | { kind: "picked"; selection: GoogleDriveSelection }
  | { kind: "cancelled" }
  | { kind: "failed"; reason: GoogleDriveFailureReason }
  | { kind: "invalid" };

/**
 * A refusal and a dismissal are different answers. Google reports a denied
 * consent through the success callback with no token, and a closed popup
 * through the error callback, so the token seam has to carry three states
 * rather than a token-or-nothing.
 */
export type GoogleDriveTokenResult =
  | { kind: "token"; accessToken: string }
  | { kind: "cancelled" }
  | { kind: "failed"; reason: GoogleDriveFailureReason };

/** Google's own OAuth answers that mean its side failed, not the person's. */
const GOOGLE_SIDE_TOKEN_ERRORS = new Set(["server_error", "temporarily_unavailable"]);

const unresponsive = (): { kind: "failed"; reason: "unresponsive" } => ({ kind: "failed", reason: "unresponsive" });

export interface GoogleDrivePickerRuntime {
  loadIdentity: () => Promise<void>;
  requestToken: (
    clientId: string,
    scope: string,
    providerSubjectHint?: string,
    signal?: AbortSignal,
  ) => Promise<GoogleDriveTokenResult>;
  loadPicker: () => Promise<void>;
  openPicker: (
    config: GoogleDrivePickerConfig,
    accessToken: string,
    signal?: AbortSignal,
  ) => Promise<unknown>;
}

interface TokenResponse {
  access_token?: string;
  error?: string;
}

interface TokenErrorResponse {
  type?: string;
}

interface TokenClient {
  requestAccessToken(options?: { prompt?: string }): void;
}

interface PickerInstance {
  setVisible(visible: boolean): void;
  dispose?(): void;
}

/**
 * The `DocsView` setters ADR 0025's container scoping needs. They are declared
 * optional because the Picker library is loaded at runtime from Google: a build
 * that dropped one must degrade to the unparented chooser rather than throw
 * inside the builder, where the failure would reach the instructor as a dead
 * button.
 *
 * `setEnableDrives` is declared but deliberately never called. See
 * `scopeViewToContainer`.
 */
interface DocsView {
  setParent?(folderId: string): DocsView;
  setIncludeFolders?(include: boolean): DocsView;
  setEnableDrives?(enable: boolean): DocsView;
  setLabel?(label: string): DocsView;
  setMode?(mode: unknown): DocsView;
}

/**
 * Google's upload tab. Optional for the same reason the `DocsView` setters
 * are: a library without it, or without `setParent`, leaves the chooser
 * without the tab rather than uploading somewhere IMAM did not choose.
 */
interface DocsUploadView {
  setParent?(folderId: string): DocsUploadView;
  setLabel?(label: string): DocsUploadView;
}

interface PickerBuilder {
  setOAuthToken(token: string): PickerBuilder;
  setDeveloperKey(key: string): PickerBuilder;
  setAppId(appId: string): PickerBuilder;
  setOrigin(origin: string): PickerBuilder;
  addView(view: unknown): PickerBuilder;
  setCallback(callback: (response: unknown) => void): PickerBuilder;
  build(): PickerInstance;
}

export interface GoogleTokenClientFactory {
  initTokenClient(config: {
    client_id: string;
    scope: string;
    include_granted_scopes: boolean;
    login_hint?: string;
    hd?: string;
    callback: (response: TokenResponse) => void;
    error_callback: (error?: TokenErrorResponse) => void;
  }): TokenClient;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: GoogleTokenClientFactory;
      };
      picker?: {
        Action: { PICKED: string; CANCEL: string };
        DocsView: new (viewId: unknown) => DocsView;
        DocsUploadView?: new () => DocsUploadView;
        DocsViewMode?: { LIST?: unknown };
        PickerBuilder: new () => PickerBuilder;
        ViewId: { DOCS: unknown };
      };
    };
    gapi?: {
      load(
        name: string,
        options: { callback: () => void; onerror: () => void; timeout: number; ontimeout: () => void },
      ): void;
    };
    __IMAM_GOOGLE_DRIVE_PICKER_TEST__?: () => Promise<unknown> | unknown;
  }
}

/**
 * Resolve exactly once, and never later than `timeoutMs`.
 *
 * Both Google seams can call back more than once — the Picker emits lifecycle
 * actions before a terminal one — and both can go quiet forever when a popup is
 * abandoned or blocked. First answer wins, and the ceiling guarantees an answer.
 */
function settleOnce<T>(
  timeoutMs: number,
  onTimeout: () => T,
  start: (settle: (value: T) => void) => void,
  signal?: AbortSignal,
  onAbort?: () => T,
): Promise<T> {
  return new Promise<T>((resolve) => {
    let settled = false;
    const finish = (value: T) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
      resolve(value);
    };
    const abort = () => finish(onAbort ? onAbort() : onTimeout());
    const timer = setTimeout(() => finish(onTimeout()), timeoutMs);
    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) { abort(); return; }
    try {
      start(finish);
    } catch (error) {
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
      throw error;
    }
  });
}

function readAction(response: unknown): string {
  if (!response || typeof response !== "object") return "";
  const action = (response as Record<string, unknown>).action;
  return typeof action === "string" ? action : "";
}

function validProviderSubjectHint(value: string | null | undefined): string | undefined {
  return value && value.length <= 255 && IDENTIFIER.test(value) ? value : undefined;
}

const HOSTED_DOMAIN = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

function loadScript(id: string, source: string, ready: () => boolean): Promise<void> {
  if (ready()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");
    /**
     * A request that is never answered fires neither `load` nor `error` — a
     * blocked script, a captive portal, and a dead network all do exactly that —
     * so without a bound this promise stays pending for the life of the page.
     * That already stranded the chooser's busy state, leaving the trigger
     * disabled with no way back. It now matters more: the chooser holds the
     * out-of-tree overlay window open for its whole duration, so an unbounded
     * load would also suspend outside-click and Escape dismissal on every record
     * sheet in the application, with no chooser on screen to explain why.
     *
     * The other two Google steps are already bounded — `gapi.load` at 10s and
     * the token popup at `GOOGLE_DRIVE_TOKEN_TIMEOUT_MS` — so this closes the
     * one remaining unbounded wait in the chooser.
     */
    const finish = (error?: Error) => {
      window.clearTimeout(timer);
      script.removeEventListener("load", loaded);
      script.removeEventListener("error", failed);
      if (error) {
        // A finished or abandoned script will never fire load again. Keeping
        // it made every later attempt time out until the page was refreshed.
        script.remove();
        reject(error);
      } else resolve();
    };
    const loaded = () => finish(ready() ? undefined : new Error("Google script loaded without its API"));
    const failed = () => finish(new Error("Google script failed to load"));
    const timer = window.setTimeout(() => finish(new Error("Google script timed out")), GOOGLE_DRIVE_SCRIPT_TIMEOUT_MS);
    script.addEventListener("load", loaded, { once: true });
    script.addEventListener("error", failed, { once: true });
    if (!existing) {
      script.id = id;
      script.src = source;
      script.async = true;
      script.defer = true;
      document.head.append(script);
    }
  });
}

/**
 * Ask Google for an access token.
 *
 * Exported so the request itself can be proved against a stand-in for Google's
 * library rather than only against a stand-in for this function. The scope and
 * `include_granted_scopes: false` are the whole privacy claim of this feature:
 * asserting them anywhere else asserts nothing. Without the second, a token
 * would also carry every scope the person granted this client before, such as
 * an earlier `drive.readonly` consent.
 */
export function requestGoogleDriveToken(
  oauth: GoogleTokenClientFactory,
  clientId: string,
  scope: string,
  providerSubjectHint?: string,
  signal?: AbortSignal,
  /** The school's Workspace domain, so Google offers only the school account. */
  hostedDomain?: string,
): Promise<GoogleDriveTokenResult> {
  const loginHint = validProviderSubjectHint(providerSubjectHint);
  const hd = hostedDomain && HOSTED_DOMAIN.test(hostedDomain) ? hostedDomain : undefined;
  return settleOnce<GoogleDriveTokenResult>(
    GOOGLE_DRIVE_TOKEN_TIMEOUT_MS,
    unresponsive,
    (settle) => {
      const client = oauth.initTokenClient({
        client_id: clientId,
        scope,
        include_granted_scopes: false,
        ...(loginHint ? { login_hint: loginHint } : {}),
        ...(hd ? { hd } : {}),
        callback: (response) => {
          if (response.access_token) {
            settle({ kind: "token", accessToken: response.access_token });
            return;
          }
          // Google routes a refused consent here, as `error: "access_denied"`.
          // The person answered the question, so this is a failure to report,
          // not a dismissal to pass over in silence. An answer with no token
          // and no Google-side error is the person's account state (not
          // signed in, no account chosen), never an outage.
          const code = response.error ?? "";
          settle({
            kind: "failed",
            reason: code === "access_denied"
              ? "declined"
              : GOOGLE_SIDE_TOKEN_ERRORS.has(code) ? "unresponsive" : "no_token",
          });
        },
        error_callback: (error) => {
          // Closing the popup is the one answer that means "never mind".
          // A popup the browser refused to open, or anything Google cannot
          // name, is a failure the reader needs to see.
          if (error?.type === "popup_closed") settle({ kind: "cancelled" });
          else settle({ kind: "failed", reason: error?.type === "popup_failed_to_open" ? "popup_blocked" : "no_token" });
        },
      });
      client.requestAccessToken({ prompt: "" });
    },
    signal,
    () => ({ kind: "cancelled" }),
  );
}

/**
 * Exported so the seams that talk to Google can be exercised against a
 * stand-in for Google, rather than replaced wholesale by a stand-in for
 * themselves.
 */
export const googleDriveBrowserRuntime: GoogleDrivePickerRuntime = {
  async loadIdentity() {
    await loadScript(
      "imam-google-identity",
      "https://accounts.google.com/gsi/client",
      () => typeof window.google?.accounts?.oauth2?.initTokenClient === "function",
    );
  },
  requestToken(clientId, scope, providerSubjectHint, signal) {
    const oauth = window.google?.accounts?.oauth2;
    if (!oauth) return Promise.resolve<GoogleDriveTokenResult>(unresponsive());
    return requestGoogleDriveToken(oauth, clientId, scope, providerSubjectHint, signal);
  },
  async loadPicker() {
    await loadScript(
      "imam-google-api",
      "https://apis.google.com/js/api.js",
      () => typeof window.gapi?.load === "function",
    );
    await new Promise<void>((resolve, reject) => {
      window.gapi?.load("picker", {
        callback: resolve,
        onerror: () => reject(new Error("Google Picker failed to load")),
        timeout: 10_000,
        ontimeout: () => reject(new Error("Google Picker timed out")),
      });
    });
  },
  openPicker(config, accessToken, signal) {
    const picker = window.google?.picker;
    if (!picker) return Promise.reject(new Error("Google Picker is unavailable"));
    let instance: PickerInstance | null = null;
    /**
     * Google builds the chooser as an iframe whose URL carries the access
     * token, and leaves that iframe in the document after the chooser closes.
     * Disposing is what removes it, so the token stops being readable out of
     * the DOM once the chooser is done. Without this, one token-bearing frame
     * accumulates per chooser opened.
     */
    const teardown = () => {
      const closing = instance;
      instance = null;
      if (!closing) return;
      // A chooser nobody answered must not be left covering the page.
      try { closing.setVisible(false); } catch { /* already torn down */ }
      // Disposing inside Google's own callback tears the chooser down
      // mid-dispatch, so let that callback finish first.
      setTimeout(() => {
        try { closing.dispose?.(); } catch { /* already torn down */ }
      }, 0);
    };
    // Resolve only once teardown has actually run. Disposing removes the
    // element that holds focus, so a caller that restores focus before that
    // happens loses it again the moment the frame goes.
    const settled = settleOnce<unknown>(
      GOOGLE_DRIVE_PICKER_TIMEOUT_MS,
      () => {
        teardown();
        return { action: PICKER_ACTION_ERROR };
      },
      (settle) => {
        const builder = new picker.PickerBuilder()
          .setOAuthToken(accessToken)
          .setDeveloperKey(config.apiKey)
          .setAppId(config.appId)
          .setOrigin(window.location.origin);
        for (const view of buildPickerViews(picker, config.parentFolderId)) {
          builder.addView(view);
        }
        instance = builder
          .setCallback((response) => {
            // The Picker reports its own lifecycle through this callback, so
            // the first call is not the answer. Only a terminal action is.
            const action = readAction(response);
            if (
              action !== PICKER_ACTION_PICKED
              && action !== PICKER_ACTION_CANCEL
              && action !== PICKER_ACTION_ERROR
            ) return;
            teardown();
            settle(response);
          })
          .build();
        instance.setVisible(true);
      },
      signal,
      () => { teardown(); return { action: PICKER_ACTION_CANCEL }; },
    );
    return settled.then((response) => new Promise<unknown>((resolve) => {
      setTimeout(() => resolve(response), 0);
    }));
  },
};

/**
 * The views the chooser offers, in tab order.
 *
 * The person's own Drive is always first and always present. A course
 * container is an ADDITIONAL tab, never a replacement: ADR 0025 states the
 * container is "an affordance, not a dependency" and that "picker-selected
 * references from anywhere in Drive remain complete and supported", and a
 * single container-scoped view withdraws exactly that. It shipped that way in
 * PR #638 and reached production, where an instructor on a course with a
 * `ready` container could no longer pick from their own Drive at all.
 *
 * Own-Drive-first preserves the pre-container landing place. Issue #616's
 * provider investigation disproved the root-membership hypothesis: the
 * parent-only view reaches a folder shared with a non-member. Folder access
 * still depends on the selected Google account and the folder's actual ACL.
 *
 * Upload from this device is the third tab, and only beside a ready container
 * (owner decision D9, 2026-11-03). The file lands in the course folder, so
 * Drive owns its bytes and the institution owns its place, and Google returns
 * it as an ordinary pick: IMAM stores the same reference it stores for any
 * other Drive file. Without a container there is no institutional place to
 * put it, so there is no Upload tab, and the person's own Drive is not a
 * destination IMAM offers. The folder is the gate: only teaching surfaces
 * pass one, so a chooser opened without a folder (a learner's link source,
 * D2) offers My Drive alone.
 */
export function buildPickerViews(
  picker: {
    DocsView: new (viewId: unknown) => DocsView;
    DocsUploadView?: new () => DocsUploadView;
    DocsViewMode?: { LIST?: unknown };
    ViewId: { DOCS: unknown };
  },
  parentFolderId?: string | null,
): unknown[] {
  const own = listed(new picker.DocsView(picker.ViewId.DOCS), picker);
  // Two DOCS tabs carry the same default label, so the pair is only legible
  // once each is named. Optional for the same reason the scoping setters are.
  const container = listed(new picker.DocsView(picker.ViewId.DOCS), picker);
  if (!scopeViewToContainer(container, parentFolderId)) return [own];
  own.setLabel?.(OWN_DRIVE_VIEW_LABEL);
  container.setLabel?.(CONTAINER_VIEW_LABEL);
  const upload = uploadViewInto(picker, parentFolderId as string);
  return upload ? [own, container, upload] : [own, container];
}

/**
 * Browsing tabs open as a list of names and type icons, not a thumbnail grid.
 * Under `drive.file` Google serves no preview for a file the app has not been
 * given, so the grid showed Docs, Sheets and PDFs as blank checkerboards
 * (Phase 1 row F4, measured 2026-11-04 on `portal.mufidseminary.org`). The
 * owner accepted the narrower scope with list view the same day. Optional for
 * the same reason the scoping setters are: a library without the mode keeps
 * its default rather than failing the chooser.
 */
function listed(
  view: DocsView,
  picker: { DocsViewMode?: { LIST?: unknown } },
): DocsView {
  const list = picker.DocsViewMode?.LIST;
  if (list !== undefined) view.setMode?.(list);
  return view;
}

export const OWN_DRIVE_VIEW_LABEL = "My Drive";
export const CONTAINER_VIEW_LABEL = "Course folder";
export const UPLOAD_VIEW_LABEL = "Upload to course folder";

/**
 * An upload tab that writes into the already validated course folder, or
 * nothing. An upload view without a parent would put the file in the person's
 * own My Drive, which is the personal-ownership outcome ADR 0025 exists to
 * avoid, so a library that cannot parent the tab gets no tab.
 */
function uploadViewInto(
  picker: { DocsUploadView?: new () => DocsUploadView },
  folderId: string,
): DocsUploadView | null {
  if (typeof picker.DocsUploadView !== "function") return null;
  const upload = new picker.DocsUploadView();
  if (typeof upload.setParent !== "function") return null;
  upload.setParent(folderId);
  upload.setLabel?.(UPLOAD_VIEW_LABEL);
  return upload;
}

/**
 * Point one view at a course's institutional container (ADR 0025) instead of
 * the person's own Drive. Never the only view - see `buildPickerViews`.
 *
 * **`setEnableDrives` is not called here, and calling it would break this.**
 * Measured 2026-09-04 against the live Picker library on
 * `portal.mufidseminary.org`, by serializing views through Google's own
 * `PickerBuilder.toUri()` and reading the `nav` parameter it produces:
 *
 * | setter order | serialized view |
 * | --- | --- |
 * | `setParent`, `setIncludeFolders`, `setEnableDrives` | `{"includeFolders":true,"dr":true}` |
 * | `setEnableDrives`, `setIncludeFolders`, `setParent` | `{"includeFolders":true,"parent":"..."}` |
 * | `setParent`, `setIncludeFolders` | `{"parent":"...","includeFolders":true}` |
 *
 * `setParent` and `setEnableDrives` are **mutually exclusive**: each erases the
 * other, and the last call wins. PR #638 called `setEnableDrives` last, so the
 * parent never reached Google at all and the chooser degraded to a plain
 * shared-drives browser - which, for an instructor who is a member of no shared
 * drive, is an empty drive selector and "No documents." That is the whole of
 * the production defect, and it is a client configuration bug rather than
 * anything about the container's audience.
 *
 * The comment this replaced claimed `setEnableDrives` was "required because
 * every container lives inside the tenant's root *shared* drive". That is
 * measured false. The container resolves for a non-member through their
 * "Shared with me" corpus - Drive's own web UI lists this container's children
 * for `acceptance.instructor@mufidseminary.org`, who is a member of zero shared
 * drives - so `setParent` alone is both sufficient and the only option that
 * survives serialization.
 *
 * `setIncludeFolders` is what makes the session folders beneath a course folder
 * reachable, which is the only way a session's material is pickable at all
 * given containers nest. It composes with `setParent`; only `setEnableDrives`
 * does not.
 *
 * A missing, malformed, or unsupported value is not an error. It leaves the
 * unparented ADR 0009 chooser exactly as it was, because the container is an
 * affordance and never a dependency.
 */
export function scopeViewToContainer(view: DocsView, parentFolderId?: string | null): boolean {
  if (
    typeof parentFolderId !== "string"
    || parentFolderId.length < 1
    || parentFolderId.length > 256
    || !IDENTIFIER.test(parentFolderId)
    || typeof view.setParent !== "function"
    || typeof view.setIncludeFolders !== "function"
  ) return false;
  view.setParent(parentFolderId);
  view.setIncludeFolders(true);
  return true;
}

function validDocument(document: unknown): GoogleDriveSelection | null {
  if (!document || typeof document !== "object") return null;
  const value = document as Record<string, unknown>;
  const fileId = typeof value.id === "string" ? value.id : "";
  const fileUrl = typeof value.url === "string" ? value.url : "";
  const mimeType = typeof value.mimeType === "string" ? value.mimeType : "";
  const name = typeof value.name === "string" ? value.name : "";
  const resourceKey = value.resourceKey === undefined || value.resourceKey === ""
    ? null
    : typeof value.resourceKey === "string" ? value.resourceKey : "";
  if (
    fileId.length < 1
    || fileId.length > 256
    || !IDENTIFIER.test(fileId)
    || fileUrl.length < 1
    || fileUrl.length > 2048
    || mimeType.length < 1
    || mimeType.length > 255
    || !MIME_TYPE.test(mimeType)
    || UNSUPPORTED_MIME_TYPES.has(mimeType)
    || name.length > 500
    || resourceKey === ""
    || (resourceKey !== null && (resourceKey.length > 256 || !IDENTIFIER.test(resourceKey)))
  ) return null;
  try {
    const parsed = new URL(fileUrl);
    if (
      parsed.protocol !== "https:"
      || parsed.username !== ""
      || parsed.password !== ""
      || !ALLOWED_GOOGLE_HOSTS.has(parsed.hostname.toLowerCase())
      || (parsed.port !== "" && parsed.port !== "443")
      || !parsed.pathname.split("/").includes(fileId)
    ) return null;
  } catch {
    return null;
  }
  const rawSize = value.sizeBytes;
  const sizeBytes = typeof rawSize === "number" && Number.isSafeInteger(rawSize) && rawSize >= 0
    ? rawSize
    : null;
  return { fileUrl, fileId, resourceKey, mimeType, name, sizeBytes };
}

export function mapGooglePickerResponse(response: unknown): GoogleDrivePickerOutcome {
  const action = readAction(response);
  if (action === PICKER_ACTION_CANCEL) return { kind: "cancelled" };
  // The Picker's own error action, and the ceiling that stands in for an
  // answer that never came, are Google's side failing.
  if (action === PICKER_ACTION_ERROR) return unresponsive();
  const value = response as Record<string, unknown>;
  if (action !== PICKER_ACTION_PICKED || !Array.isArray(value.docs) || value.docs.length !== 1) {
    return { kind: "invalid" };
  }
  const selection = validDocument(value.docs[0]);
  return selection ? { kind: "picked", selection } : { kind: "invalid" };
}

export async function chooseGoogleDriveFile(
  config: GoogleDrivePickerConfig,
  runtime: GoogleDrivePickerRuntime = googleDriveBrowserRuntime,
  signal?: AbortSignal,
): Promise<GoogleDrivePickerOutcome> {
  if (!config.enabled || signal?.aborted) return { kind: "cancelled" };
  // Every surface below this line — the identity popup, the token popup, and
  // Google's own chooser — is painted outside React's tree, so the sheet or
  // dialog the trigger lives in has to stop reading clicks inside them as its
  // own dismissal. See `external-overlay.ts`.
  return withExternalOverlay(async () => {
    if (!signal) return chooseWithGoogle(config, runtime);
    let cancel!: () => void;
    const cancelled = new Promise<GoogleDrivePickerOutcome>(resolve => {
      cancel = () => resolve({ kind: "cancelled" });
    });
    signal.addEventListener("abort", cancel, { once: true });
    try {
      // A script request cannot be recalled, but its late response must never
      // open Google over another account, route, or a cancelled editor.
      return await Promise.race([chooseWithGoogle(config, runtime, signal), cancelled]);
    } finally { signal.removeEventListener("abort", cancel); }
  });
}

async function chooseWithGoogle(
  config: GoogleDrivePickerConfig,
  runtime: GoogleDrivePickerRuntime,
  signal?: AbortSignal,
): Promise<GoogleDrivePickerOutcome> {
  let token: GoogleDriveTokenResult = { kind: "cancelled" };
  try {
    if (import.meta.env.DEV && window.__IMAM_GOOGLE_DRIVE_PICKER_TEST__) {
      return mapGooglePickerResponse(await window.__IMAM_GOOGLE_DRIVE_PICKER_TEST__());
    }
    await runtime.loadIdentity();
    if (signal?.aborted) return { kind: "cancelled" };
    const providerSubjectHint = validProviderSubjectHint(config.providerSubjectHint);
    token = signal
      ? await runtime.requestToken(config.oauthClientId, GOOGLE_DRIVE_FILE_SCOPE, providerSubjectHint, signal)
      : providerSubjectHint
      ? await runtime.requestToken(config.oauthClientId, GOOGLE_DRIVE_FILE_SCOPE, providerSubjectHint)
      : await runtime.requestToken(config.oauthClientId, GOOGLE_DRIVE_FILE_SCOPE);
    if (signal?.aborted) return { kind: "cancelled" };
    if (token.kind !== "token") return token;
    await runtime.loadPicker();
    if (signal?.aborted) return { kind: "cancelled" };
    return mapGooglePickerResponse(await (signal
      ? runtime.openPicker(config, token.accessToken, signal)
      : runtime.openPicker(config, token.accessToken)));
  } catch {
    // A loader, a popup, or the Picker itself can fail outright. None of those
    // touch the draft, and none of them are the reader's doing.
    return unresponsive();
  } finally {
    // The only token reference lives in this function's active chooser scope.
    token = { kind: "cancelled" };
  }
}
